import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Dialog, Grid, IconButton, Stack, Typography } from "@mui/material";
import moment from "moment";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RouterIcon from "../../../../assets/icons/Router Connection New.svg";
import Button from "../../../../components/Button/Button";
import { LoadingSpinner } from "../../../../components/shared/LoadingSpinner/LoadingSpinner";
import { FALLBACK_BANNERS } from "../../../../Constants/constants";
import { StyledCard } from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";

const EquipmentReturnInfo = () => {
  const [expanded, setExpanded] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);

  const {
    returnRequired,
    equipment,
    email,
    returnDate,
    returnCode,
    orderType,
    status,
    loading,
    apiError,
    completedURL,
  } = useSelector((state: any) => state.equipmentReturnDetails);

  useEffect(() => {
    if (status?.toLowerCase() === "pending") {
      setExpanded(true);
    }
  }, [status]);

  const { accountNumber } = useSelector((state: any) => state.customerInfoDetails);
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );

  if (!returnRequired || apiError) return null;
  if (loading) return <LoadingSpinner />;

  const formattedDate = returnDate ? moment(returnDate).format("MMMM D, YYYY") : "";

  const isPending = status?.toLowerCase() === "pending";
  const isCompleted = status?.toLowerCase() === "completed";
  const returnEquipmentScenarios =
    sideCoreBannerMessages?.ReturnEquipment?.["return-equipment-scenarios"] ??
    FALLBACK_BANNERS.ReturnEquipment?.["return-equipment-scenarios"];

  const normalizedOrderType = orderType?.toLowerCase();

  const returnEquipmentMessage =
    normalizedOrderType === "change"
      ? returnEquipmentScenarios?.["change-order"]
      : normalizedOrderType === "disconnected" || normalizedOrderType === "disconnect"
        ? returnEquipmentScenarios?.["disconnected-accounts"]
        : "";

  return (
    <StyledCard sx={{ mb: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setExpanded((prev) => !prev)}>
        <Typography
          fontWeight={500}
          fontSize="16px"
          color={Theme.palette.secondary.main}
          letterSpacing="0.5px">
          EQUIPMENT RETURN INFO
        </Typography>

        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {expanded ? (
            <ExpandLessIcon sx={{ color: Theme.palette.secondary.main }} />
          ) : (
            <ExpandMoreIcon sx={{ color: Theme.palette.secondary.main }} />
          )}
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Box mt={2}>
          <Typography fontWeight={700} fontSize="14px" mb={0.5}>
            HERE&apos;S THE EQUIPMENT YOU NEED TO RETURN
          </Typography>

          {returnEquipmentMessage && (
            <Typography
              fontSize="12px"
              color={Theme.palette.customcolor.profileTypoGraphyColor}
              mb={2}>
              {returnEquipmentMessage}
            </Typography>
          )}

          {/* Account info */}
          <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 1, sm: 4 }} mb={2}>
            <Box>
              <Typography
                fontSize="11px"
                fontWeight={600}
                color={Theme.palette.customcolor.cancelbtntext}
                letterSpacing="0.5px">
                ACCOUNT NUMBER :
              </Typography>

              <Typography
                fontSize="13px"
                fontWeight="bold"
                fontFamily="DM Sans"
                color={Theme.palette.customcolor.blackColor}>
                {accountNumber}
              </Typography>
            </Box>

            {!isCompleted && email && (
              <Box>
                <Typography
                  fontSize="11px"
                  fontWeight={600}
                  color={Theme.palette.customcolor.cancelbtntext}
                  letterSpacing="0.5px">
                  CONFIRMATION SENT TO :
                </Typography>

                <Typography fontWeight={600} fontSize="14px">
                  {email}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Devices to return heading */}
          {isPending && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography fontWeight={700} fontSize="14px">
                DEVICES TO RETURN
              </Typography>
            </Stack>
          )}

          {/* Device cards */}
          <Grid container spacing={2} mb={2}>
            {equipment?.map((item: any, index: number) => (
              <Grid item xs={12} sm={6} key={`${item.serialNumber}-${index}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    bgcolor: Theme.palette.customcolor.returnEquipmentBg,
                    borderRadius: "8px",
                    p: 1.5,
                  }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "8px",
                      bgcolor: Theme.palette.customcolor.cancelbtn,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                    <Box
                      component="img"
                      src={RouterIcon}
                      alt="router device"
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    />
                  </Box>

                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={700} fontSize="14px" noWrap>
                      {item.make} {item.modelName}
                    </Typography>

                    <Typography fontSize="12px" color={Theme.palette.customcolor.cancelbtntext}>
                      SN {item.serialNumber}
                    </Typography>
                  </Box>

                  {item?.equipmentStatus && (
                    <Typography
                      fontWeight={600}
                      fontSize="12px"
                      color={
                        item.equipmentStatus === "completed"
                          ? "primary.main"
                          : Theme.palette.customcolor.blackColor
                      }
                      sx={{
                        flexShrink: 0,
                        textTransform: "capitalize",
                        borderRadius: "16px",
                        px: 1.25,
                        py: 0.5,
                        bgcolor:
                          item.equipmentStatus === "completed"
                            ? Theme.palette.customcolor.homepageBannerHover
                            : Theme.palette.customcolor.returnPendingBackground,
                      }}>
                      {item.equipmentStatus === "completed" ? "Return Completed" : "Pending"}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            ))}
          </Grid>

          {/* Active return */}
          {isPending && (
            <>
              {/* Return date */}
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  bgcolor: "#edf7ed",
                  border: "1px solid #c3e6c3",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.5,
                  mb: 2,
                }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  <AccessTimeIcon sx={{ color: "primary.main" }} />
                </Box>

                {/* your text here */}
                <Box>
                  <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color="primary.main"
                    letterSpacing="0.5px">
                    RETURN BY
                  </Typography>

                  <Typography fontWeight={700} fontSize="16px" color="primary.main">
                    {formattedDate}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  border: `1px solid ${Theme.palette.customcolor.accordionborder}`,
                  borderRadius: "8px",
                  p: 2,
                  mb: 2,
                }}>
                <Typography fontWeight={700} fontSize="14px" mb={0.5}>
                  FREE RETURN LABEL
                </Typography>

                <Typography
                  fontSize="13px"
                  color={Theme.palette.customcolor.profileTypoGraphyColor}
                  mb={returnCode ? 1 : 0}>
                  No printer or packaging needed. Simply bring your item to any UPS location and
                  drop it off.
                </Typography>

                {returnCode && (
                  <>
                    <Stack direction="row" gap={1} alignItems="center" mb={1.5}>
                      <Typography
                        fontSize="12px"
                        color={Theme.palette.customcolor.cancelbtntext}
                        fontWeight={600}
                        letterSpacing="0.3px">
                        RETURN CODE
                      </Typography>

                      <Typography fontSize="13px" fontWeight={700}>
                        {returnCode}
                      </Typography>
                    </Stack>

                    <Button
                      type="root"
                      title="OPEN RETURN LABEL"
                      onClick={() => setLabelOpen(true)}
                    />
                  </>
                )}
              </Box>
            </>
          )}

          {/* Completed return */}
          {completedURL && (
            <Box mb={2}>
              <Button
                type="root"
                title="CHECK UPS TRACKING INFO"
                onClick={() => window.open(completedURL, "_blank", "noopener,noreferrer")}
              />
            </Box>
          )}

          <Typography
            fontSize="12px"
            color={Theme.palette.customcolor.cancelbtntext}
            sx={{ fontStyle: "normal" }}>
            Keep your confirmation email for reference. Devices not received by the return date may
            be billed at replacement cost.
          </Typography>
        </Box>
      </Collapse>

      {returnCode && (
        <Dialog
          open={labelOpen}
          onClose={() => setLabelOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "480px",
              maxWidth: "calc(100% - 32px)",
              borderRadius: "16px",
              boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 3.5,
              pt: 3.5,
              pb: 2.5,
            }}>
            <Typography fontWeight={700} fontSize="20px" color="#242424">
              Return Label
            </Typography>

            <IconButton
              onClick={() => setLabelOpen(false)}
              size="small"
              aria-label="Close return label"
              sx={{
                width: 24,
                height: 24,
                p: 0,
                bgcolor: "#4A4A4A",
                color: "#FFFFFF",
                "&:hover": {
                  bgcolor: "#333333",
                },
              }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>

          <Stack
            alignItems="center"
            sx={{
              px: 3.5,
              pb: 3.5,
            }}>
            <Box
              sx={{
                width: 370,
                height: 370,
                maxWidth: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${Theme.palette.customcolor.accordionborder}`,
                borderRadius: "12px",
                boxSizing: "border-box",
              }}>
              <QRCodeSVG value={returnCode} size={250} level="M" />
            </Box>

            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{
                mt: 2.5,
                bgcolor: Theme.palette.customcolor.menuitembackground,
                borderRadius: "8px",
                px: 2,
                py: 1,
              }}>
              <Typography
                fontSize="13px"
                fontWeight={600}
                color={Theme.palette.customcolor.cancelbtntext}>
                UPS Return Code :
              </Typography>

              <Typography fontSize="13px" fontWeight={700} color={Theme.palette.secondary.main}>
                {returnCode}
              </Typography>
            </Stack>
          </Stack>
        </Dialog>
      )}
    </StyledCard>
  );
};

export default EquipmentReturnInfo;
